import { useTranslation } from 'react-i18next';
import { Tag } from '@/lib/ui/Tag';
import { MonoValue, Eyebrow } from '@/lib/ui/Typography';
import { formatRange, readEstimate } from './estimate';

/**
 * "Estimate" section of a contact lead that came from /pricing: the package, each
 * priced line, the range and the scope the visitor chose. Hidden for every other
 * lead. Amounts are USD and render LTR inside an Arabic console.
 */
export function LeadEstimatePanel({ meta }: { meta: unknown }) {
  const { t, i18n } = useTranslation();
  const est = readEstimate(meta);
  if (!est) return null;
  const from = t('leads.estimate.from');
  const money = (low: number, high: number | null) => (
    <MonoValue>
      <bdi dir="ltr">{formatRange(low, high, from)}</bdi>
    </MonoValue>
  );
  const scope = [
    est.input.pages > 0 ? t('leads.estimate.pages', { count: est.input.pages }) : null,
    est.input.bilingual ? t('leads.estimate.bilingual') : null,
    est.input.integrations > 0 ? t('leads.estimate.integrations', { count: est.input.integrations }) : null,
    est.input.mobileApp ? t('leads.estimate.line.mobile_app') : null,
    est.input.inheritedCode ? t('leads.estimate.line.inherited_code') : null,
    est.input.replacingLive ? t('leads.estimate.line.live_replacement') : null,
  ].filter(Boolean);

  return (
    <section
      aria-labelledby="lead-estimate-heading"
      className="flex flex-col gap-md border-t-[0.5px] border-hairline pt-lg"
    >
      <div className="flex flex-wrap items-center gap-sm">
        <h3 id="lead-estimate-heading">
          <Eyebrow>{t('leads.estimate.heading')}</Eyebrow>
        </h3>
        {est.kind === 'priced' ? (
          <Tag variant="teal">{formatRange(est.low, est.high, from)}</Tag>
        ) : (
          <Tag variant="neutral">{t(`leads.estimate.unpriced.${est.reason}`)}</Tag>
        )}
        {est.kind === 'priced' && est.custom && <Tag variant="neutral">{t('leads.estimate.custom')}</Tag>}
      </div>
      {est.kind === 'priced' ? (
        <dl className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <div>
            <dt className="eyebrow text-ink-mute">{t('leads.estimate.package')}</dt>
            <dd className="mt-2xs text-small text-ink-graphite">
              {(i18n.language === 'ar' && est.name.ar) || est.name.en || est.input.package}
            </dd>
          </div>
          {est.care && (
            <div>
              <dt className="eyebrow text-ink-mute">{t('leads.estimate.care')}</dt>
              <dd className="mt-2xs text-small text-ink-graphite">
                {(i18n.language === 'ar' && est.care.name.ar) || est.care.name.en} ·{' '}
                <MonoValue>
                  <bdi dir="ltr">{formatRange(est.care.price, est.care.price, from)}</bdi>
                </MonoValue>{' '}
                {t('leads.estimate.perMonth')}
              </dd>
            </div>
          )}
          {est.lines.map((l) => (
            <div key={l.key}>
              <dt className="eyebrow text-ink-mute">{t(`leads.estimate.line.${l.key}`, { defaultValue: l.key })}</dt>
              <dd className="mt-2xs text-small text-ink-graphite">{money(l.low, l.high)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-small text-ink-mute">
          {t('leads.estimate.package')}: <bdi dir="ltr">{est.input.package || '—'}</bdi>
        </p>
      )}
      {scope.length > 0 && <p className="text-small text-ink-graphite">{scope.join(' · ')}</p>}
    </section>
  );
}
